import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import WrappedHeader from './WrappedHeader/WrappedHeader'
import type { SlideConfig } from '../types/wrapped'
import {
  WrappedStoryActions,
  useWrappedStorySelector,
  useWrappedStoryDispatch,
} from 'contexts/Wrapped'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'

// Slides
import IntroSlide from './Slides/IntroSlide'
import TravelPersonalitySlide from './Slides/TravelPersonalitySlide'
import DestinationsSlide from './Slides/DestinationsSlide'
import NightsSpentSlide from './Slides/NightsSpentSlide'
import DistanceSlide from './Slides/DistanceSlide'
import TopDestinationsQuizSlide from './Slides/TopDestinationsQuizSlide'
import OfferViewsSlide from './Slides/OfferViewsSlide'
import TopOfferSlide from './Slides/TopOfferSlide'
import SavingsSlide from './Slides/SavingsSlide'
import LoyaltySlide from './Slides/LoyaltySlide'
import SummarySlide from './Slides/SummarySlide'
import PredictionSlide from './Slides/PredictionSlide'

const SLIDE_COMPONENT_MAP: Record<SlideConfig['id'], React.ComponentType> = {
  'intro': IntroSlide,
  'personality': TravelPersonalitySlide,
  'destinations': DestinationsSlide,
  'nights': NightsSpentSlide,
  'distance': DistanceSlide,
  'top-destinations-quiz': TopDestinationsQuizSlide,
  'offer-views': OfferViewsSlide,
  'top-offer': TopOfferSlide,
  'savings': SavingsSlide,
  'loyalty': LoyaltySlide,
  'summary': SummarySlide,
  'prediction': PredictionSlide,
}

const StyledPane = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  background-color: ${props => props.theme.palette.neutral.default.one};
  overflow: hidden;

  &:focus {
    outline: none;
  }

  .is-full-page & {
    max-width: none;
  }
`

const SlideWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const SlideContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const SlideLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
  will-change: opacity;
  transform: translateZ(0);
  content-visibility: hidden;
  contain-intrinsic-size: 100% 100%;

  &.active {
    opacity: 1;
    pointer-events: auto;
    overflow-y: auto;
    z-index: 1;
    content-visibility: visible;
  }
`

const NavigationArrow = styled(IconButton)`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  transition: opacity 0.2s;
  opacity: 0;
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }

  &.left {
    left: ${rem(12)};
  }

  &.right {
    right: ${rem(12)};
  }

  &.visible {
    opacity: 0.6;
    pointer-events: auto;

    &:hover, &:focus {
      opacity: 0.9;
    }
  }
`

const TAP_ZONE_WIDTH_FRACTION = 0.25

interface Props {
  mode: 'modal' | 'fullPage'
  onClose?: () => void
}

function WrappedStoryContent({ mode, onClose }: Props) {
  const modalElement = useModalElementContext()
  const dispatch = useWrappedStoryDispatch()
  const isMobile = useIsMobileDevice()

  const currentIndex = useWrappedStorySelector(state => state.currentIndex)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const data = useWrappedStorySelector(state => state.data)
  const slides = useWrappedStorySelector(state => state.slides)
  const currentSlide = useWrappedStorySelector(state => state.currentSlide)
  const phase = useWrappedStorySelector(state => state.phase)

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const slideInfoRef = useRef({ currentIndex, slides })
  slideInfoRef.current = { currentIndex, slides }

  const slidesLengthRef = useRef(slides.length)
  slidesLengthRef.current = slides.length

  const hasVideo = !!currentSlide?.video

  useEffect(() => {
    if (hasVideo) {
      dispatch({ type: WrappedStoryActions.setVideoReady, isVideoReady: false })
    } else {
      dispatch({ type: WrappedStoryActions.setVideoReady, isVideoReady: true })
    }
  }, [currentIndex, hasVideo, dispatch])

  const handleClose = useCallback(() => {
    if (mode === 'fullPage' && onClose) {
      onClose()
    } else {
      modalElement?.resolve()
    }
  }, [modalElement, mode, onClose])

  const goToNextSlide = useCallback(() => {
    const { currentIndex } = slideInfoRef.current
    const slidesLength = slidesLengthRef.current
    if (currentIndex < slidesLength - 1) {
      dispatch({ type: WrappedStoryActions.setCurrentSlide, index: currentIndex + 1 })
    }
  }, [dispatch])

  const goToPrevSlide = useCallback(() => {
    const { currentIndex } = slideInfoRef.current
    dispatch({ type: WrappedStoryActions.setCurrentSlide, index: Math.max(0, currentIndex - 1) })
  }, [dispatch])

  const goToSlide = useCallback((index: number) => {
    dispatch({ type: WrappedStoryActions.setCurrentSlide, index })
  }, [dispatch])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { currentIndex, slides } = slideInfoRef.current
    const currentSlide = slides[currentIndex]
    const scrollTop = e.currentTarget.scrollTop
    dispatch({
      type: WrappedStoryActions.setScrolled,
      isScrolled: scrollTop > 0 && !currentSlide?.imageId && !currentSlide?.video,
    })
  }, [dispatch])

  const [hoverZone, setHoverZone] = useState<'left' | 'right' | null>(null)

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const width = rect.width

    if (x < width * TAP_ZONE_WIDTH_FRACTION) {
      setHoverZone('left')
    } else if (x > width * (1 - TAP_ZONE_WIDTH_FRACTION)) {
      setHoverZone('right')
    } else {
      setHoverZone(null)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoverZone(null)
  }, [])

  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-interactive-zone]')) {
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const width = rect.width

    if (x < width * TAP_ZONE_WIDTH_FRACTION) {
      goToPrevSlide()
    } else if (x > width * (1 - TAP_ZONE_WIDTH_FRACTION)) {
      goToNextSlide()
    }
  }, [goToPrevSlide, goToNextSlide])

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowRight':
        goToNextSlide()
        break
      case 'ArrowLeft':
        goToPrevSlide()
        break
      case ' ':
        event.preventDefault()
        dispatch({ type: WrappedStoryActions.togglePause, isPaused: undefined })
        break
      case 'Escape':
        handleClose()
        break
    }
  }, [goToNextSlide, goToPrevSlide, handleClose, dispatch])

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    if (isPaused || phase === 'question' || currentSlide?.duration === 0 || !isVideoReady || currentSlide?.waitForClick) {
      return
    }

    timerRef.current = setTimeout(() => {
      goToNextSlide()
    }, currentSlide?.duration ?? 5000)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [currentIndex, isPaused, currentSlide, goToNextSlide, isVideoReady, phase])

  const slideElements = useMemo(() => {
    return slides.map(slide => {
      const SlideComponent = SLIDE_COMPONENT_MAP[slide.id]
      return SlideComponent ? <SlideComponent key={slide.id} /> : null
    })
  }, [slides])

  useEffect(() => {
    containerRef.current?.focus()
  }, [])

  if (!data) return null

  return (
    <StyledPane
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Year in Review story"
    >
      <WrappedHeader
        onClose={handleClose}
        onGoToSlide={goToSlide}
      />
      <SlideWrapper>
        <SlideContent
          onClick={handleContentClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {slides.map((slide, index) => {
            const isNearby = Math.abs(index - currentIndex) <= 1
            if (isMobile && !isNearby) return null

            const isActive = index === currentIndex
            return (
              <SlideLayer
                key={slide.id}
                className={clsx({ active: isActive })}
                onScroll={isActive ? handleScroll : undefined}
              >
                {slideElements[index]}
              </SlideLayer>
            )
          })}
          {currentIndex > 0 && (
            <NavigationArrow
              className={clsx('left', { visible: hoverZone === 'left' })}
              onClick={goToPrevSlide}
              kind="primary"
              variant="ghost"
              shape="circle"
              size="large"
              aria-label="Previous slide"
            >
              <LineAngleLeftIcon />
            </NavigationArrow>
          )}
          {currentIndex < slides.length - 1 && (
            <NavigationArrow
              className={clsx('right', { visible: hoverZone === 'right' })}
              onClick={goToNextSlide}
              kind="primary"
              variant="ghost"
              shape="circle"
              size="large"
              aria-label="Next slide"
            >
              <LineAngleRightIcon />
            </NavigationArrow>
          )}
        </SlideContent>
      </SlideWrapper>
    </StyledPane>
  )
}

export default WrappedStoryContent
